import { useState, useEffect, useCallback, useMemo } from "react";
import {
  format,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  addDays,
  subDays,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
} from "date-fns";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronLeft, ChevronRight, Bell } from "lucide-react";
import { useCalendars, useCalendarEvents } from "@/hooks/useCalendar";
import type { CalendarEvent } from "@/hooks/useCalendar";
import { CalendarFilterBar } from "@/components/calendar/CalendarSidebar";
import { CalendarMonthView } from "@/components/calendar/CalendarMonthView";
import { CalendarWeekView } from "@/components/calendar/CalendarWeekView";
import { CalendarDayView } from "@/components/calendar/CalendarDayView";
import { CalendarGanttView } from "@/components/calendar/CalendarGanttView";
import { CalendarEventDetailSheet } from "@/components/calendar/CalendarEventDetailSheet";
import { CreateCalendarEventDialog } from "@/components/calendar/CreateCalendarEventDialog";
import { CalendarNotificationSettingsDialog } from "@/components/calendar/CalendarNotificationSettingsDialog";

type ViewType = "month" | "week" | "day" | "gantt";

const STORAGE_KEY = "calendar-selected-ids";

function loadSelectedIds(): string[] | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function saveSelectedIds(ids: string[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
}

export default function CalendarPage() {
  const [viewType, setViewType] = useState<ViewType>("month");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedCalendarIds, setSelectedCalendarIds] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);

  // Detail sheet state
  const [detailEvent, setDetailEvent] = useState<CalendarEvent | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Edit dialog state
  const [editEvent, setEditEvent] = useState<CalendarEvent | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  // Day click / context menu defaults for new events
  const [defaultDate, setDefaultDate] = useState<Date | null>(null);
  const [defaultCalendarId, setDefaultCalendarId] = useState<string | undefined>(undefined);
  const [createOpen, setCreateOpen] = useState(false);

  // Calendar notification settings
  const [notifSettingsOpen, setNotifSettingsOpen] = useState(false);

  // Data
  const { data: calendars = [], isLoading: calendarsLoading } = useCalendars();

  // Compute date range for query based on view
  const { rangeStart, rangeEnd } = useMemo(() => {
    let s: Date, e: Date;
    if (viewType === "month") {
      s = startOfWeek(startOfMonth(currentDate));
      e = endOfWeek(endOfMonth(currentDate));
    } else if (viewType === "week") {
      s = startOfWeek(currentDate);
      e = endOfWeek(currentDate);
    } else if (viewType === "gantt") {
      // Gantt manages its own scale, but we fetch a wide range (6 months from current)
      s = startOfMonth(currentDate);
      e = endOfMonth(addMonths(currentDate, 5));
    } else {
      s = currentDate;
      e = currentDate;
    }
    return {
      rangeStart: s.toISOString(),
      rangeEnd: new Date(e.getTime() + 86400000).toISOString(),
    };
  }, [currentDate, viewType]);

  const { data: events = [], isLoading: eventsLoading } = useCalendarEvents(
    selectedCalendarIds,
    rangeStart,
    rangeEnd
  );

  // Initialize selected IDs from localStorage or default to all
  useEffect(() => {
    if (calendars.length > 0 && !initialized) {
      const stored = loadSelectedIds();
      if (stored && stored.length > 0) {
        const valid = stored.filter((id) => calendars.some((c) => c.id === id));
        setSelectedCalendarIds(valid.length > 0 ? valid : calendars.map((c) => c.id));
      } else {
        setSelectedCalendarIds(calendars.map((c) => c.id));
      }
      setInitialized(true);
    }
  }, [calendars, initialized]);

  // Persist selection changes
  useEffect(() => {
    if (initialized) {
      saveSelectedIds(selectedCalendarIds);
    }
  }, [selectedCalendarIds, initialized]);

  // Navigation
  const goBack = useCallback(() => {
    setCurrentDate((d) => {
      if (viewType === "month" || viewType === "gantt") return subMonths(d, 1);
      if (viewType === "week") return subWeeks(d, 1);
      return subDays(d, 1);
    });
  }, [viewType]);

  const goForward = useCallback(() => {
    setCurrentDate((d) => {
      if (viewType === "month" || viewType === "gantt") return addMonths(d, 1);
      if (viewType === "week") return addWeeks(d, 1);
      return addDays(d, 1);
    });
  }, [viewType]);

  const goToday = useCallback(() => setCurrentDate(new Date()), []);

  const toggleCalendar = useCallback((id: string) => {
    setSelectedCalendarIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedCalendarIds((prev) =>
      prev.length === calendars.length ? [] : calendars.map((c) => c.id)
    );
  }, [calendars]);

  const handleEventClick = useCallback((ev: CalendarEvent) => {
    setDetailEvent(ev);
    setDetailOpen(true);
  }, []);

  const handleEdit = useCallback((ev: CalendarEvent) => {
    setDetailOpen(false);
    setEditEvent(ev);
    setEditOpen(true);
  }, []);

  const handleDayClick = useCallback((day: Date) => {
    setDefaultDate(day);
  }, []);

  const handleAddEvent = useCallback((day: Date, calendarId?: string) => {
    setDefaultDate(day);
    setDefaultCalendarId(calendarId);
    setCreateOpen(true);
  }, []);

  // Period label
  const periodLabel = useMemo(() => {
    if (viewType === "month" || viewType === "gantt") return format(currentDate, "MMMM yyyy");
    if (viewType === "week") {
      const ws = startOfWeek(currentDate);
      const we = endOfWeek(currentDate);
      return `${format(ws, "MMM d")} – ${format(we, "MMM d, yyyy")}`;
    }
    return format(currentDate, "EEEE, MMMM d, yyyy");
  }, [currentDate, viewType]);

  const isLoading = calendarsLoading || eventsLoading;

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      {/* Header — clean, just title + new event */}
      <header className="flex-none flex h-16 items-center gap-4 border-b bg-card px-6">
        <SidebarTrigger />
        <div className="flex-1">
          <h1 className="text-lg font-semibold text-foreground">Calendar</h1>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setNotifSettingsOpen(true)}
          title="Calendar notification settings"
        >
          <Bell className="h-4 w-4 mr-2" />
          Notifications
        </Button>

        <CreateCalendarEventDialog
          calendars={calendars}
          defaultDate={defaultDate}
        />

        {/* Context-menu-triggered create dialog (controlled) */}
        <CreateCalendarEventDialog
          calendars={calendars}
          defaultDate={defaultDate}
          defaultCalendarId={defaultCalendarId}
          open={createOpen}
          onOpenChange={(v) => {
            setCreateOpen(v);
            if (!v) setDefaultCalendarId(undefined);
          }}
        />
      </header>

      {/* Toolbar area — fixed height */}
      <div className="flex-none px-6 pt-4 pb-2 space-y-3">
        {/* Toolbar row: nav + view toggle + period label */}
        <div className="flex items-center justify-between gap-4">
          {/* Left: navigation */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={goBack}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" className="h-8" onClick={goToday}>
              Today
            </Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={goForward}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <span className="text-sm font-semibold ml-2">
              {periodLabel}
            </span>
          </div>

          {/* Right: view toggle */}
          <div className="flex border rounded-lg overflow-hidden">
            {(["month", "week", "day", "gantt"] as ViewType[]).map((v) => (
              <button
                key={v}
                className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                  viewType === v
                    ? "bg-primary text-primary-foreground"
                    : "bg-card text-muted-foreground hover:bg-accent"
                }`}
                onClick={() => setViewType(v)}
              >
                {v === "gantt" ? "Gantt" : v}
              </button>
            ))}
          </div>
        </div>

        {/* Calendar filter pills */}
        <CalendarFilterBar
          calendars={calendars}
          selectedIds={selectedCalendarIds}
          onToggle={toggleCalendar}
          onToggleAll={toggleAll}
        />
      </div>

      {/* Calendar grid — fills remaining height */}
      <div className="flex-1 min-h-0 flex">
        <div className="flex-1 min-w-0 px-6 pb-4">
          {isLoading ? (
            <div className="space-y-2 h-full">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="flex-1 w-full h-full" />
            </div>
          ) : viewType === "month" ? (
            <CalendarMonthView
              currentDate={currentDate}
              events={events}
              calendars={calendars}
              onEventClick={handleEventClick}
              onDayClick={handleDayClick}
              onAddEvent={handleAddEvent}
            />
          ) : viewType === "week" ? (
            <CalendarWeekView
              currentDate={currentDate}
              events={events}
              calendars={calendars}
              onEventClick={handleEventClick}
            />
          ) : viewType === "gantt" ? (
            <CalendarGanttView
              currentDate={currentDate}
              events={events}
              calendars={calendars}
              onEventClick={handleEventClick}
            />
          ) : (
            <CalendarDayView
              currentDate={currentDate}
              events={events}
              calendars={calendars}
              onEventClick={handleEventClick}
            />
          )}
        </div>
      </div>

      {/* Detail sheet */}
      <CalendarEventDetailSheet
        event={detailEvent}
        calendars={calendars}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onEdit={handleEdit}
      />

      {/* Edit dialog */}
      <CreateCalendarEventDialog
        calendars={calendars}
        editEvent={editEvent}
        open={editOpen}
        onOpenChange={(v) => {
          setEditOpen(v);
          if (!v) setEditEvent(null);
        }}
      />

      {/* Calendar notification settings */}
      <CalendarNotificationSettingsDialog
        open={notifSettingsOpen}
        onOpenChange={setNotifSettingsOpen}
      />
    </div>
  );
}
